import Paper from '@mui/material/Paper'
import { validateEmail } from 'global/helpers/validators'
import { useContext, useEffect, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

// @mui imports //
import Alert from '@mui/material/Alert'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Box from '@mui/material/Box'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import Container from '@mui/material/Container'
import DangerousIcon from '@mui/icons-material/Dangerous'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import { type Theme } from '@mui/material/styles'
import { type SystemStyleObject } from '@mui/system'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'

// KN imports //
import theme from 'assets/theme'
import KNLoader from 'components/KNLoader/KNLoader'

// Modules //
import TheHulk from 'modules/TheHulk/TheHulk'

// Functional //
import { analyticsEvent, analyticsPageView } from 'global/helpers/analytics'
import { getLogo } from 'global/helpers/getLogo'

// Context //
import { UserContext } from 'context/authentication/UserContext'

// Data //
import { loginTranslations } from './Login.data'
import Footer from 'modules/Footer/Footer'

const Login = (): ReactElement => {
  // Screen specific
  const [screenState, setScreenState] = useState<'logIn' | 'forgotPassword' | 'passwordReset' | 'loading'>('logIn')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const handleTogglePasswordVisibility = (): void => {
    setShowPassword(!showPassword)
  }
  const [forgottenEmail, setForgottenEmail] = useState('')
  const [errorMessageOpen, setErrorMessageOpen] = useState(false)

  // Data
  useTranslation()
  const { translation } = loginTranslations()

  // Context
  const { loginUser, userLoading, userError, resetUser, userResetResult } = useContext(UserContext)

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    setScreenState('loading')
    await loginUser(email, password)
    analyticsEvent('polestar_user_login')
  }

  const resetPassword = async (): Promise<void> => {
    setScreenState('loading')
    await resetUser(forgottenEmail)
    setScreenState('passwordReset')
    analyticsEvent('polestar_user_reset_password')
  }

  useEffect(() => {
    analyticsPageView('polestar/login')
  }, [])

  useEffect(() => {
    if (userError) {
      setScreenState('logIn')
      setErrorMessageOpen(true)
    }
  }, [userError])

  // Reset values by screen change
  useEffect(() => {
    setEmail('')
    setPassword('')
    setForgottenEmail('')
  }, [screenState])

  return (
    <Container maxWidth="xs">
      <TheHulk placement="login" />
      <Paper sx={{
        position: 'relative',
        padding: '40px',
        marginTop: 8,
        marginBottom: 2,
      }}>
        <Box my={3}>
          {/* Login State */}
          {screenState === 'logIn' && (
            <>
              {errorMessageOpen && (
                <Alert variant="filled" severity="error" sx={{ mb: 2 }}>
                  {userError}
                </Alert>
              )}
              <form onSubmit={onSubmit}>
                {getLogo('logo', '100%', '64px', theme.palette.primary.main)}
                <Box mt={9}>
                  <Stack
                    spacing={2}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="p3" color="primary.light">
                      {translation.applicationDescription}
                    </Typography>
                  </Stack>
                </Box>
                <Stack spacing={2} px={{ xs: 0, sm: 2 }}>
                  <Box>
                    <Typography
                      variant="p3"
                      mb={0.5}
                      sx={{
                        color: ({ palette: { grey } }: Theme): string => grey[600],
                        fontWeight: ({ typography: { fontWeightLight } }: Theme): number => fontWeightLight,
                      }}
                    >
                      {translation.email}
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      type="email"
                      autoComplete="email"
                      slotProps={{
                        htmlInput: {
                          'data-test': 'email',
                        },
                      }}
                      onChange={(e): void => setEmail(e.target.value)}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="p3"
                      mb={0.5}
                      sx={{
                        color: ({ palette: { grey } }: Theme): string => grey[600],
                        fontWeight: ({ typography: { fontWeightLight } }: Theme): number => fontWeightLight,
                      }}
                    >
                      {translation.password}
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={password}
                      onChange={(e): void => setPassword(e.target.value)}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <IconButton
                              onClick={handleTogglePasswordVisibility}
                              edge="end"
                              aria-label="toggle password visibility"
                              sx={({ palette: { grey } }): SystemStyleObject<Theme> => ({
                                color: grey[300],
                                transition: 'color 200ms',
                                '&:hover': {
                                  color: grey[400],
                                },
                              })}
                            >
                              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          ),
                        },
                      }}
                    />
                  </Box>
                  <Box
                    my={2}
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button
                      variant="text"
                      color="primary"
                      onClick={(): void => {
                        setScreenState('forgotPassword')
                        analyticsEvent('polestar_forgotten_password_button_clicked')
                      }}
                    >
                      {translation.forgottenPassword}
                    </Button>
                  </Box>
                  <Box my={2}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={!validateEmail(email)}
                    >
                      {translation.loginButton}
                    </Button>
                  </Box>
                </Stack>
              </form>
            </>
          )}
          {/* Forgot Password State */}
          {screenState === 'forgotPassword' && (
            <form onSubmit={async (): Promise<void> => await resetPassword()}>
              {getLogo('logo', '100%', '64px', theme.palette.primary.main)}
              <Box my={3}>
                <Stack
                  spacing={2}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="h4" color="black">
                    {translation.forgottenPassword}?
                  </Typography>
                  <Typography variant="p3" color="grey">
                    {translation.resetDescription}
                  </Typography>
                </Stack>
              </Box>
              <Stack spacing={2} px={5}>
                <Box>
                  <Typography
                    variant="p3"
                    mb={0.5}
                    sx={{
                      color: ({ palette: { grey } }: Theme): string => grey[600],
                      fontWeight: ({ typography: { fontWeightLight } }: Theme): number => fontWeightLight,
                    }}
                  >
                    {translation.email}
                  </Typography>
                  <TextField variant="outlined" fullWidth onChange={(e): void => setForgottenEmail(e.target.value)} />
                </Box>
                <Box my={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={!validateEmail(forgottenEmail)}
                  >
                    {translation.resetPasswordButton}
                  </Button>
                </Box>
                <Button
                  variant="text"
                  color="primary"
                  onClick={(): void => setScreenState('logIn')}
                  startIcon={<ArrowBackIcon />}
                >
                  {translation.backButton}
                </Button>
              </Stack>
            </form>
          )}
          {/* Password Reset Message State */}
          {screenState === 'passwordReset' && (
            <Stack
              spacing={2}
              sx={{
                display: 'flex',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              {userResetResult === 'success' ? (
                <CheckCircleIcon color="success" sx={{ height: '100px', width: '100px' }} />
              ) : (
                <DangerousIcon color="error" sx={{ height: '100px', width: '100px' }} />
              )}
              <Box my={3}>
                <Typography variant="h4" color="primary.main">
                  {userResetResult === 'success' ? translation.resetSuccessful : translation.resetFail}
                </Typography>
                <Typography variant="p3" color="primary.light">
                  {userResetResult === 'success' ? translation.resetSuccessfulMsg : translation.resetFailMsg}
                </Typography>
              </Box>
              <Button
                variant="text"
                color="primary"
                onClick={(): void => setScreenState('logIn')}
                startIcon={<ArrowBackIcon />}
              >
                {translation.backButton}
              </Button>
            </Stack>
          )}
          {/* Loading State */}
          {(userLoading || screenState === 'loading') && (
            <KNLoader>
              <Typography> {translation.loading}</Typography>
            </KNLoader>
          )}
        </Box>
      </Paper>
      <Footer />
    </Container>
  )
}

export default Login
